import { Component, OnDestroy, OnInit } from '@angular/core';
import { MapStoreMunicipality } from '@app/shared/models/municipality/Municipality';
import { BuildingService } from '@app/energy-map/services/building.service';
import { BuildingsStoreService } from '@app/energy-map/services/buildings-store.service';
import { MapStoreService } from '@app/energy-map/services/map-store.service';
import { BuildingTypes } from '@app/shared/models/building-types';
import { GetAddressPipe } from '@app/shared/pipes/get-address.pipe';
import { BuildingTypesService } from '@app/shared/services/api/building-types.services';
import { AuthService } from '@app/shared/services/auth.service';
import { OverlayLoaderService } from '@app/shared/services/overlay-loader.service';
import { UserMunicipalitiesService } from '@app/shared/services/user-municipalities.service';
import * as L from 'leaflet';
import { isNil, orderBy } from 'lodash';
import { BehaviorSubject, fromEvent, Observable, of, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map, takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'app-edit-building',
  templateUrl: './edit-building.component.html',
  styleUrls: ['./edit-building.component.scss'],
  providers: [GetAddressPipe],
})
export class EditBuildingComponent implements OnInit, OnDestroy {
  protected unsubscribe$: Subject<void> = new Subject<void>();

  private userMunicipalitySubscription?: Subscription;

  buildingStatus: string = '';

  selectedBuildingType: any;

  selectedBuildingStore?: BehaviorSubject<any> = new BehaviorSubject(undefined);

  selectedBuilding = this.selectedBuildingStore?.asObservable();

  buildingTypes: any[] = [];

  isBuildingDetaillsValid: Observable<boolean> = of(false);

  private modalVisibilityStore = new BehaviorSubject(false);

  private visiblePageStore: BehaviorSubject<any> = new BehaviorSubject(undefined);

  readonly isModalVisible = this.modalVisibilityStore.getValue();

  readonly isModalVisible$ = this.modalVisibilityStore.asObservable();

  readonly visiblePage$ = this.visiblePageStore.asObservable();

  marker: L.Marker | null = null;

  constructor(
    public authService: AuthService,
    private buildingStoreService: BuildingsStoreService,
    private buildingTypesService: BuildingTypesService,
    private mapStoreService: MapStoreService,
    private userMunicipalitiesService: UserMunicipalitiesService,
    private building: BuildingService,
    private overlayLoaderService: OverlayLoaderService
  ) {}

  ngOnInit() {
    this.buildingStoreService.selectedBuildingWithUnkownTypeOrGps = null;

    this.showModalWhenBuildingSelected()
      .pipe(
        tap(() => (this.overlayLoaderService.isLoading = true)),
        tap((x) => this.selectedBuildingStore?.next(x)),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(() =>
        setTimeout(() => {
          this.overlayLoaderService.isLoading = false;
        }, 1500)
      );

    this.buildingTypesService
      .getAll()
      .pipe(
        filter((types) => !!types),
        map((results) => orderBy(results, 'name')),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((buildingTypes: BuildingTypes[]) => {
        this.buildingTypes = buildingTypes;
        this.buildingTypes.sort((a, b) => {
          return a.name.localeCompare(b.name);
        });
      });

    this.userMunicipalitySubscription = this.userMunicipalitiesService.currentMunicipality$
      .pipe(
        filter((municipality: MapStoreMunicipality) => !!municipality?.guid && !!municipality?.key),
        distinctUntilChanged()
      )
      .subscribe(() => this.close());

    this.selectedBuilding
      ?.pipe(
        filter((x) => !!x),
        map(this.setPageToDisplay),
        takeUntil(this.unsubscribe$)
      )
      .subscribe();

    this.selectedBuilding
      ?.pipe(
        filter((x) => !!x),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((building: any) => (this.buildingStatus = building.status));

    this.selectedBuilding
      ?.pipe(
        filter((x) => !!x),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((x) => {
        x.marker.remove();
      });

    this.buildingStoreService.selectedBuildingWithUnkownTypeOrGps$
      .pipe(
        filter((x) => !!x),
        filter((x: any) => x.marker.options.data.latitude > 0 && x.marker.options.data.longitude > 0),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((x) => {
        if (this.marker) {
          this.marker.remove();
        }

        this.building.gotoBuilding(x.marker);
        this.building.removeMarkerFromCluster(x.marker);
        this.marker = this.createMarker(this.createHighlightIcon(x));
        this.marker.setLatLng([x.marker.options.data.latitude, x.marker.options.data.longitude]);
        this.marker.addTo(this.mapStoreService.map as L.Map);
        this.marker.dragging?.disable();
      });

    this.selectedBuilding
      ?.pipe(
        filter((x) => !!x),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((x) => (this.selectedBuildingType = x.marker.options.data.buildingType));
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.userMunicipalitySubscription?.unsubscribe();
  }

  compareObjects = (buildingType1: any, buildingType2: any): boolean => {
    return buildingType1.guid === buildingType2.guid;
  };

  setPageToDisplay = (building: any) => (building.status === 'nogps' ? this.visiblePageStore.next('nogps') : this.visiblePageStore.next('notype'));

  showModalWhenBuildingSelected = () =>
    this.buildingStoreService.selectedBuildingWithUnkownTypeOrGps$.pipe(
      filter(() => isNil(this.buildingStoreService.selectedBuilding)),
      filter((x) => !!x),
      tap((x) => this.modalVisibilityStore.next(true))
    );

  close = () => {
    this.modalVisibilityStore.next(false);
    this.buildingStoreService.selectedBuildingWithUnkownTypeOrGps = null;

    if (this.marker) {
      (this.mapStoreService.map as L.Map).removeLayer(this.marker);
      this.marker = null;
    }

    const building = this.selectedBuildingStore?.getValue();
    if (building) {
      this.building.addMarkerToCluster(building?.marker);
      this.selectedBuildingStore?.next(undefined);
    }
  };

  open = () => this.modalVisibilityStore.next(true);

  changeModalVisibility = (isVisible: boolean) => this.modalVisibilityStore.next(isVisible);

  trackByFn = (_: number, building: any) => building.guid;

  next = () => this.visiblePageStore.next('nogps');

  back = (building: any) => {
    if (this.marker) {
      if (building.marker.options.data.latitude >= 0 && building.marker.options.data.longitude) {
        this.marker.setIcon(this.createHighlightIcon(building));
        this.marker.dragging?.disable();
      } else {
        (this.mapStoreService.map as L.Map).removeLayer(this.marker);
        this.marker = null;
      }
    }

    this.visiblePageStore.next('notype');
  };

  save = (building: any) => {
    if (this.marker) {
      building.marker.setLatLng(this.marker.getLatLng());
      building.marker.setIcon(this.createNormalIcon(building));
      (this.mapStoreService.map as L.Map).removeLayer(this.marker);
      this.marker = null;
    }

    this.buildingStoreService.updateBuildingType(building.marker.options.data, this.selectedBuildingType);

    building.marker.options.data.longitude = building.marker.getLatLng().lng;
    building.marker.options.data.latitude = building.marker.getLatLng().lat;
    this.buildingStoreService.saveBuilding(building);
    this.close();
  };

  localizeObject = (building: any) => {
    if (this.marker) {
      (this.mapStoreService.map as L.Map).removeLayer(this.marker);
    }

    this.marker = this.createMarker(this.createDragIcon(building)).addTo((this.mapStoreService.map as L.Map)!);
    (this.mapStoreService.map as L.Map).setZoom(17).panTo((this.mapStoreService.map as L.Map).getCenter());
    this.onBuildingDrag(this.marker)
      .pipe(
        tap(() => this.modalVisibilityStore.next(false)),
        takeUntil(this.unsubscribe$)
      )
      .subscribe();

    this.onBuildingDragEnd(this.marker)
      .pipe(
        tap(() => this.modalVisibilityStore.next(true)),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((e: any) => (this.mapStoreService.map as L.Map).panTo(e.target.getLatLng()));
  };

  moveMapToCenter = () => (this.mapStoreService.map as L.Map).setZoom(17).panTo((this.mapStoreService.map as L.Map).getCenter());

  createMarker = (icon: any) =>
    L.marker((this.mapStoreService.map as L.Map).getCenter(), {
      icon: icon,
      draggable: true,
    });

  onBuildingDrag = (marker: any) => fromEvent(marker, 'drag');

  onBuildingDragEnd = (marker: any) => fromEvent(marker, 'dragend');

  getIconImageName = (building: any) => {
    const buildingMarkerIconImage = building.marker.options.data.buildingType ? building.marker.options.data.buildingType.imageName : 'cube-v2.png';

    return this.selectedBuildingType ? this.selectedBuildingType.imageName : buildingMarkerIconImage;
  };

  getHighlightedIconUrl = (imageName: string) => `/assets/icons/buildings/highlight/${imageName}`;

  getDragIconUrl = (imageName: string) => `/assets/icons/buildings/drag/${imageName}`;

  getNormalIconUrl = (imageName: string) => `/assets/icons/objectTypes/${imageName}`;

  createDragIcon = (building: any) => L.icon({ iconUrl: this.getDragIconUrl(this.getIconImageName(building)) });

  createHighlightIcon = (building: any) =>
    L.icon({
      iconUrl: this.getHighlightedIconUrl(this.getIconImageName(building)),
      iconSize: [120, 150],
      iconAnchor: [30, 120],
    });

  createNormalIcon = (building: any) => L.icon({ iconUrl: this.getNormalIconUrl(this.getIconImageName(building)) });
}
